import React, { useState, useRef, useEffect } from "react";
import {
  DndContext,
  useDraggable,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
} from "@dnd-kit/core";
import TableCard from "../components/TableCard";
import TableModal from "../components/TableModal";
import Sidebar from "../components/Sidebar";
import "../styles/Dashboard.css";
import FloorplanPrintModal from "../components/FloorplanPrintModal";

const Dashboard = ({ user, onLogout }) => {
  const imgRef = useRef(null);

  const [tables, setTables] = useState([]);
  const [allTables, setAllTables] = useState([]);
  const [error, setError] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [imgScale, setImgScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [layoutMode, setLayoutMode] = useState(false);
  const [showForbidden] = useState(false);
  const [layoutOptions, setLayoutOptions] = useState({
    applyType: "permanent",  // "permanent" ili "date"
    date: new Date().toLocaleDateString("en-CA"),
    shift_id: "",
  });
  const [originalTables, setOriginalTables] = useState([]);
  const [confirmLayout, setConfirmLayout] = useState({ show: false, type: "", message: "", onConfirm: null });
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, reservations: [], tableLabel: "" });
  const [showPrintFloorplan, setShowPrintFloorplan] = useState(false);
  const [currentFloor, setCurrentFloor] = useState(
    localStorage.getItem('defaultFloor') || 'inside'
  );  
  const handleFloorChange = (floor) => {
    setCurrentFloor(floor);
    localStorage.setItem('defaultFloor', floor);
  };

  useEffect(() => {
    setTables(prev => prev.filter(t => 
      currentFloor === "inside" ? !t.is_outdoor : t.is_outdoor
    ));
  }, [currentFloor]);

  const forbiddenZonesInside = [
    { name: "Up", x: 0, y: 0, width: 1560, height: 80 },
    { name: "Down", x: 0, y: 860, width: 1560, height: 100 },
    { name: "Left", x: 0, y: 0, width: 80, height: 960 },
    { name: "Right", x: 1500, y: 0, width: 60, height: 960 },
    { name: "Šank", x: 320, y: 560, width: 400, height: 160 },
    { name: "Sećija", x: 960, y: 680, width: 540, height: 42 },
    { name: "Terasa_desno", x: 780, y: 80, width: 722, height: 260 },
    { name: "Terasa_lijevo", x: 80, y: 80, width: 80, height: 260 },
    { name: "Pregrada_medjuprostor", x: 160, y: 338, width: 677, height: 0.1 },
    { name: "Pregradni_zid_1", x: 780, y: 340, width: 20, height: 120 },
    { name: "Pregradni_zid_2", x: 840, y: 600, width: 20, height: 120 },
    { name: "Donji_WC_dio", x: 320, y: 720, width: 1180, height: 140 },
  ];

  const forbiddenZonesOutside = [
    { name: "Up", x: 140, y: 100, width: 1320, height: 60 },
    { name: "Down", x: 280, y: 840, width: 1100, height: 20 },
    { name: "Left", x: 0, y: 0, width: 140, height: 960 },
    { name: "Right", x: 1260, y: 160, width: 2000, height: 700 },
    { name: "Medjuprostor", x: 420, y: 660, width: 600, height: 180 },
  ];

  const forbiddenZones = currentFloor === "inside" ? forbiddenZonesInside : forbiddenZonesOutside;

  const GRID_SIZE = 20;
  const snapToGrid = (value) => Math.round(value / GRID_SIZE) * GRID_SIZE;

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const updateImageMetrics = () => {
    const img = imgRef.current;
    if (!img) return;

    const container = img.parentElement;
    const naturalRatio = img.naturalWidth / img.naturalHeight;
    const containerRatio = container.clientWidth / container.clientHeight;

    let renderWidth, renderHeight;

    if (naturalRatio > containerRatio) {
      renderWidth = container.clientWidth;
      renderHeight = container.clientWidth / naturalRatio;
    } else {
      renderHeight = container.clientHeight;
      renderWidth = container.clientHeight * naturalRatio;
    }

    const scaleX = renderWidth / img.naturalWidth;
    const scaleY = renderHeight / img.naturalHeight;
    const offsetX = (container.clientWidth - renderWidth) / 2;
    const offsetY = (container.clientHeight - renderHeight) / 2;

    setImgScale(Math.min(scaleX, scaleY));
    setOffset({ x: offsetX, y: offsetY });
  };

  const handleImageLoad = () => updateImageMetrics();

  useEffect(() => {
    window.addEventListener("resize", updateImageMetrics);
    return () => window.removeEventListener("resize", updateImageMetrics);
  }, []);

  useEffect(() => {
    const fetchTables = async () => {
      try {
        const params = new URLSearchParams();
        const dateToUse = selectedDate || new Date();
        params.append("date", dateToUse.toLocaleDateString("en-CA"));

        const response = await fetch(`/reservations/api/tables/?${params}`);
        if (!response.ok) throw new Error(`Greška: ${response.status}`);

        const data = await response.json();
        const allMapped = data.tables.map((t) => ({
          id: t.id,
          label: t.label,
          seats: t.seats,
          type: t.type,
          is_outdoor: t.is_outdoor,
          orientation: t.orientation || "horizontal",
          status: t.status || ["free"],
          x: Number(t.position_x) || 0,
          y: Number(t.position_y) || 0,
          width: Number(t.width) || 50,
          height: Number(t.height) || 40,
        }));
        setAllTables(allMapped);

        const mapped = allMapped.filter(t =>
          currentFloor === "inside" ? !t.is_outdoor : t.is_outdoor
        );
        setTables(mapped);
        setOriginalTables(mapped);
      } catch (err) {
        setError(err.message);
      }
    };

    // ✅ Ne fetchuj dok je layout mode aktivan
    if (layoutMode) return;

    fetchTables();

    const interval = setInterval(fetchTables, 30000);
    return () => clearInterval(interval);
  }, [selectedDate, layoutMode, currentFloor]); // ✅ layoutMode mora biti ovdje da se interval zaustavi

  // ✅ Poseban useEffect samo za pauziranje intervala
  useEffect(() => {
    // Kada izađeš iz layout mode-a, refetchaj stolove
    if (!layoutMode) return;
    // Nema akcije pri ulasku u layout mode
  }, [layoutMode]);

  useEffect(() => {
    // Uvijek refetchaj pri promjeni floora, čak i u layout mode-u
    const fetchTablesForFloor = async () => {
      try {
        const params = new URLSearchParams();
        const dateToUse = selectedDate || new Date();
        params.append("date", dateToUse.toLocaleDateString("en-CA"));

        const response = await fetch(`/reservations/api/tables/?${params}`);
        if (!response.ok) return;

        const data = await response.json();
        const allMapped = data.tables.map((t) => ({
          id: t.id,
          label: t.label,
          seats: t.seats,
          type: t.type,
          is_outdoor: t.is_outdoor,
          orientation: t.orientation || "horizontal",
          status: t.status || ["free"],
          x: Number(t.position_x) || 0,
          y: Number(t.position_y) || 0,
          width: Number(t.width) || 50,
          height: Number(t.height) || 40,
        }));
        setAllTables(allMapped);

        const mapped = allMapped.filter(t =>
          currentFloor === "inside" ? !t.is_outdoor : t.is_outdoor
        );
        setTables(mapped);
        setOriginalTables(mapped);
      } catch (err) {
        console.error(err);
      }
    };

    fetchTablesForFloor();
  }, [currentFloor, selectedDate]); // poziva se svaki put kad se floor promijeni

  useEffect(() => {
    const fetchShifts = async () => {
      try {
        const response = await fetch("/reservations/api/shifts/");
        if (response.ok) {
          const data = await response.json();
          setShifts(data.shifts || []);
        }
      } catch {
        setShifts([]);
      }
    };
    fetchShifts();
  }, []);

  const handleSaveLayout = async () => {
    try {
      const changedTables = tables.filter(t => {
        const orig = originalTables.find(o => o.id === t.id);
        return orig && (orig.x !== t.x || orig.y !== t.y);
      });

      if (changedTables.length > 0) {
        await Promise.all(
          changedTables.map((table) =>
            fetch(`/reservations/api/tables/${table.id}/position/`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                position_x: table.x,
                position_y: table.y,
                apply_type: layoutOptions.applyType,
                apply_date: layoutOptions.applyType === "date" ? layoutOptions.date : null,
                apply_shift_id: null,
              }),
            })
          )
        );
      }

      // ✅ Refetchaj za trenutno odabrani datum u kalendaru
      const params = new URLSearchParams();
      params.append("date", selectedDate.toLocaleDateString("en-CA"));
      const response = await fetch(`/reservations/api/tables/?${params}`);
      const data = await response.json();
      const mapped = data.tables.map((t) => ({
        id: t.id, label: t.label, seats: t.seats, type: t.type,
        orientation: t.orientation || "horizontal",
        status: t.status || ["free"],
        x: Number(t.position_x) || 0, y: Number(t.position_y) || 0,
        width: Number(t.width) || 50, height: Number(t.height) || 40,
      }));
      setTables(mapped);
      setOriginalTables(mapped);
      setLayoutMode(false);
      setConfirmLayout({ show: true, type: "success", message: "Raspored uspješno spremljen!" });
    } catch (err) {
      console.error("Greška pri spremanju rasporeda:", err);
    }
  };

  const handleResetLayout = () => {
    setConfirmLayout({
      show: true,
      type: "confirm",
      message: `Resetirati raspored za ${layoutOptions.date} na stalnu postavku?`,
      onConfirm: async () => {
        await fetch(`/reservations/api/tables/layout/reset/`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ date: layoutOptions.date }),
        });

        const params = new URLSearchParams();
        params.append("date", selectedDate.toLocaleDateString("en-CA"));
        const response = await fetch(`/reservations/api/tables/?${params}`);
        const data = await response.json();
        const mapped = data.tables.map((t) => ({
          id: t.id, label: t.label, seats: t.seats, type: t.type,
          orientation: t.orientation || "horizontal",
          status: t.status || ["free"],
          x: Number(t.position_x) || 0, y: Number(t.position_y) || 0,
          width: Number(t.width) || 50, height: Number(t.height) || 40,
        }));
        setTables(mapped);
        setOriginalTables(mapped);
        setConfirmLayout({ show: false });
      }
    });
  };

  const handleLayoutMode = () => {
    if (!layoutMode) {
      // Ulaziš u layout mode – postavi datum na odabrani u kalendaru
      setLayoutOptions({
        applyType: "permanent",
        date: selectedDate.toLocaleDateString("en-CA"),
        shift_id: "",
      });
    }
    setLayoutMode(!layoutMode);
  };

  const isInsideForbiddenZone = (x, y, width, height) =>
    forbiddenZones.some(
      (zone) =>
        x < zone.x + zone.width &&
        x + width > zone.x &&
        y < zone.y + zone.height &&
        y + height > zone.y
    );

  const findNearestFreePosition = (x, y, tableWidth, tableHeight) => {
    const candidates = [];

    for (const zone of forbiddenZones) {
      candidates.push({ x: zone.x - tableWidth, y });
      candidates.push({ x: zone.x + zone.width, y });
      candidates.push({ x, y: zone.y - tableHeight });
      candidates.push({ x, y: zone.y + zone.height });
    }

    candidates.sort((a, b) => {
      const distA = Math.sqrt((a.x - x) ** 2 + (a.y - y) ** 2);
      const distB = Math.sqrt((b.x - x) ** 2 + (b.y - y) ** 2);
      return distA - distB;
    });

    for (const candidate of candidates) {
      const snappedX = snapToGrid(candidate.x);
      const snappedY = snapToGrid(candidate.y);

      const img = imgRef.current;
      if (img) {
        if (snappedX < 0 || snappedY < 0) continue;
        if (snappedX + tableWidth > img.naturalWidth) continue;
        if (snappedY + tableHeight > img.naturalHeight) continue;
      }

      if (!isInsideForbiddenZone(snappedX, snappedY, tableWidth, tableHeight)) {
        return { x: snappedX, y: snappedY };
      }
    }

    return null;
  };

  const findNearestFreePositionFromOverlap = (x, y, tableWidth, tableHeight, tableId) => {
    const candidates = [];

    for (const other of tables) {
      if (other.id === tableId) continue;
      candidates.push({ x: other.x - tableWidth, y });
      candidates.push({ x: other.x + other.width, y });
      candidates.push({ x, y: other.y - tableHeight });
      candidates.push({ x, y: other.y + other.height });
    }

    candidates.sort((a, b) => {
      const distA = Math.sqrt((a.x - x) ** 2 + (a.y - y) ** 2);
      const distB = Math.sqrt((b.x - x) ** 2 + (b.y - y) ** 2);
      return distA - distB;
    });

    for (const candidate of candidates) {
      const snappedX = snapToGrid(candidate.x);
      const snappedY = snapToGrid(candidate.y);

      const img = imgRef.current;
      if (img) {
        if (snappedX < 0 || snappedY < 0) continue;
        if (snappedX + tableWidth > img.naturalWidth) continue;
        if (snappedY + tableHeight > img.naturalHeight) continue;
      }

      if (isInsideForbiddenZone(snappedX, snappedY, tableWidth, tableHeight)) continue;

      const overlaps = tables.some(
        (other) =>
          other.id !== tableId &&
          snappedX < other.x + other.width &&
          snappedX + tableWidth > other.x &&
          snappedY < other.y + other.height &&
          snappedY + tableHeight > other.y
      );

      if (!overlaps) return { x: snappedX, y: snappedY };
    }

    return null;
  };

  const handleDragEnd = async ({ active, delta }) => {
    const tableId = active.id;
    const movedTable = tables.find((t) => t.id === tableId);
    if (!movedTable) return;

    let newX = movedTable.x + delta.x / imgScale;
    let newY = movedTable.y + delta.y / imgScale;

    newX = snapToGrid(newX);
    newY = snapToGrid(newY);

    const tableWidth = movedTable.width;
    const tableHeight = movedTable.height;

    const img = imgRef.current;
    if (img) {
      const maxX = img.naturalWidth - tableWidth;
      const maxY = img.naturalHeight - tableHeight;
      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));
    }

    if (isInsideForbiddenZone(newX, newY, tableWidth, tableHeight)) {
      const nearest = findNearestFreePosition(newX, newY, tableWidth, tableHeight);
      if (!nearest) return;
      newX = nearest.x;
      newY = nearest.y;
    }

    const overlaps = tables.some(
      (other) =>
        other.id !== tableId &&
        newX < other.x + other.width &&
        newX + tableWidth > other.x &&
        newY < other.y + other.height &&
        newY + tableHeight > other.y
    );

    if (overlaps) {
      const nearest = findNearestFreePositionFromOverlap(newX, newY, tableWidth, tableHeight, tableId);
      if (!nearest) return;
      newX = nearest.x;
      newY = nearest.y;
    }

    setTables((prev) =>
      prev.map((t) => (t.id === tableId ? { ...t, x: newX, y: newY } : t))
    );
  };

  const tableElements = tables.map((table) => (
    <ViewTable
      key={table.id}
      x={offset.x + table.x * imgScale}
      y={offset.y + table.y * imgScale}
      table={table}
      imgScale={imgScale}
      onClick={() => setSelectedTable(table)}
      selectedDate={selectedDate}
      onTooltip={setTooltip}
    />
  ));

  return (
    <div className="dashboard-wrapper">
      <Sidebar
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        onNewReservation={() => setSelectedTable({})}
        currentFloor={currentFloor}
        onFloorChange={handleFloorChange}
        onRefetchTables={() => {
          const params = new URLSearchParams();
          params.append("date", selectedDate.toLocaleDateString("en-CA"));
          fetch(`/reservations/api/tables/?${params}`)
            .then(res => res.json())
            .then(data => {
          const allMapped = data.tables.map((t) => ({
            id: t.id,
            label: t.label,
            seats: t.seats,
            type: t.type,
            is_outdoor: t.is_outdoor,
            orientation: t.orientation || "horizontal",
            status: t.status || ["free"],
            x: Number(t.position_x) || 0,
            y: Number(t.position_y) || 0,
            width: Number(t.width) || 50,
            height: Number(t.height) || 40,
          }));
          setAllTables(allMapped);

          // Filtriraj za prikaz na tlocrtu
          const mapped = allMapped.filter(t => 
            currentFloor === "inside" ? !t.is_outdoor : t.is_outdoor
          );
          setTables(mapped);
          setOriginalTables(mapped);
            });
        }}
        onPrintFloorplan={() => setShowPrintFloorplan(true)}
        onLayoutMode={handleLayoutMode}
        layoutMode={layoutMode}
        user={user}
        onLogout={async () => {
          await fetch("/reservations/api/auth/logout/", {
            method: "POST",
            credentials: "include",
          });
          onLogout();
        }}
      />

      <div className="divider-line" />

      <div className="dashboard-container">
        {error && <div>Greška: {error}</div>}

        <img
          ref={imgRef}
          src={currentFloor === "inside" ? "/tlocrt_inside.png" : "/tlocrt_outside.png"}
          alt="Tlocrt"
          className="floor-image"
          onLoad={handleImageLoad}
        />

        {layoutMode && (
          <div
            style={{
              position: "absolute",
              left: offset.x,
              top: offset.y,
              width: imgRef.current ? imgRef.current.clientWidth : 0,
              height: imgRef.current ? imgRef.current.clientHeight : 0,
              backgroundImage: `
                linear-gradient(rgba(193, 180, 40, 0.2) 1px, transparent 1px),
                linear-gradient(90deg, rgba(193, 180, 40, 0.2) 1px, transparent 1px)
              `,
              backgroundSize: `${GRID_SIZE * imgScale}px ${GRID_SIZE * imgScale}px`,
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        )}

        {showForbidden &&
          forbiddenZones.map((zone, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: offset.x + zone.x * imgScale,
                top: offset.y + zone.y * imgScale,
                width: zone.width * imgScale,
                height: zone.height * imgScale,
                backgroundColor: "rgba(255, 0, 0, 0.15)",
                border: "1px dashed red",
                pointerEvents: "none",
              }}
              title={zone.name}
            />
          ))}

        {layoutMode ? (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            {tables.map((table) => (
              <DraggableTable
                key={table.id}
                id={table.id}
                x={offset.x + table.x * imgScale}
                y={offset.y + table.y * imgScale}
                table={table}
                imgScale={imgScale}
              />
            ))}
          </DndContext>
        ) : (
          tableElements
        )}

        {selectedTable && (
          <TableModal
            initialTable={selectedTable}
            tables={allTables}
            shifts={shifts}
            selectedDate={selectedDate}
            currentFloor={currentFloor}
            onClose={() => setSelectedTable(null)}
            onReservationCreated={() => {
              // Odmah refetchaj stolove
              const params = new URLSearchParams();
              params.append("date", selectedDate.toLocaleDateString("en-CA"));
              fetch(`/reservations/api/tables/?${params}`)
                .then(res => res.json())
                .then(data => {
                  const allMapped = data.tables.map((t) => ({
                    id: t.id, label: t.label, seats: t.seats, type: t.type,
                    is_outdoor: t.is_outdoor,
                    orientation: t.orientation || "horizontal",
                    status: t.status || ["free"],
                    x: Number(t.position_x) || 0, y: Number(t.position_y) || 0,
                    width: Number(t.width) || 50, height: Number(t.height) || 40,
                  }));
                  setAllTables(allMapped);
                  const mapped = allMapped.filter(t =>
                    currentFloor === "inside" ? !t.is_outdoor : t.is_outdoor
                  );
                  setTables(mapped);
                  setOriginalTables(mapped);
                });
            }}
          />
        )}

        {layoutMode && (
          <div style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1000,
            backgroundColor: "#111",
            border: "1px solid #c1b428",
            borderRadius: 10,
            padding: "10px 14px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
            color: "#c1b428",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}>
            <span style={{ fontWeight: "bold" }}>🗂️ Raspored:</span>

            {/* Tip primjene */}
            <button
              onClick={() => setLayoutOptions(o => ({ ...o, applyType: "permanent" }))}
              style={{
                padding: "3px 10px",
                borderRadius: 6,
                border: "1px solid #c1b428",
                backgroundColor: layoutOptions.applyType === "permanent" ? "#c1b428" : "#1f1f1f",
                color: layoutOptions.applyType === "permanent" ? "black" : "#c1b428",
                cursor: "pointer",
                fontSize: 11,
                fontWeight: "bold",
              }}
            >
              Stalna postavka
            </button>

            <button
              onClick={() => setLayoutOptions(o => ({ ...o, applyType: "date" }))}
              style={{
                padding: "3px 10px",
                borderRadius: 6,
                border: "1px solid #c1b428",
                backgroundColor: layoutOptions.applyType === "date" ? "#c1b428" : "#1f1f1f",
                color: layoutOptions.applyType === "date" ? "black" : "#c1b428",
                cursor: "pointer",
                fontSize: 11,
                fontWeight: "bold",
              }}
            >
              Za datum
            </button>

            {/* Datum – samo ako je odabrano "date" */}
            {layoutOptions.applyType === "date" && (
              <>
                <input
                  type="date"
                  value={layoutOptions.date}
                  onChange={(e) => setLayoutOptions(o => ({ ...o, date: e.target.value }))}
                  style={{
                    backgroundColor: "#1f1f1f",
                    color: "#c1b428",
                    border: "1px solid #c1b428",
                    borderRadius: 6,
                    padding: "3px 8px",
                    fontSize: 11,
                  }}
                />
                <button
                  onClick={handleResetLayout}
                  style={{
                    padding: "3px 10px",
                    borderRadius: 6,
                    border: "1px solid #888",
                    backgroundColor: "#333",
                    color: "#aaa",
                    cursor: "pointer",
                    fontSize: 11,
                  }}
                >
                  ↺ Reset
                </button>
              </>
            )}

            {/* Gumbi */}
            <button
              onClick={() => setLayoutMode(false)}
              style={{
                padding: "3px 10px",
                borderRadius: 6,
                border: "1px solid #555",
                backgroundColor: "#333",
                color: "#ccc",
                cursor: "pointer",
                fontSize: 11,
              }}
            >
              Odustani
            </button>

            <button
              onClick={handleSaveLayout}
              style={{
                padding: "3px 12px",
                borderRadius: 6,
                border: "none",
                backgroundColor: "#c1b428",
                color: "black",
                cursor: "pointer",
                fontSize: 11,
                fontWeight: "bold",
              }}
            >
              💾 Spremi
            </button>
          </div>
        )}
        {confirmLayout.show && (
          <div style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}>
            <div style={{
              backgroundColor: "#111",
              border: "1px solid #c1b428",
              borderRadius: 12,
              padding: 24,
              width: 300,
              textAlign: "center",
              color: "#c1b428",
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>
                {confirmLayout.type === "success" ? "✅" : "⚠️"}
              </div>
              <p style={{ fontSize: 13, marginBottom: 20, color: "white" }}>
                {confirmLayout.message}
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                {confirmLayout.type === "confirm" ? (
                  <>
                    <button
                      onClick={() => setConfirmLayout({ show: false })}
                      style={{
                        padding: "6px 16px",
                        borderRadius: 6,
                        border: "1px solid #555",
                        backgroundColor: "#333",
                        color: "#ccc",
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      Odustani
                    </button>
                    <button
                      onClick={confirmLayout.onConfirm}
                      style={{
                        padding: "6px 16px",
                        borderRadius: 6,
                        border: "none",
                        backgroundColor: "#c1b428",
                        color: "black",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: "bold",
                      }}
                    >
                      Potvrdi
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setConfirmLayout({ show: false })}
                    style={{
                      padding: "6px 20px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor: "#c1b428",
                      color: "black",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: "bold",
                    }}
                  >
                    OK
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {tooltip.visible && (
          <div style={{
            position: "fixed",
            left: tooltip.x + 12,
            top: tooltip.y - 10,
            zIndex: 500,
            backgroundColor: "#111",
            border: "1px solid #c1b428",
            borderRadius: 8,
            padding: "8px 12px",
            color: "#c1b428",
            fontSize: 11,
            minWidth: 180,
            maxWidth: 250,
            pointerEvents: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
          }}>
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>
              Stol {tooltip.tableLabel}
            </div>
            {tooltip.reservations.length > 0 ? (
              tooltip.reservations.map((r) => (
                <div key={r.id} style={{ borderTop: "1px solid #333", paddingTop: 4, marginTop: 4 }}>
                  <div style={{ color: "white", fontWeight: "semibold" }}>{r.guest_name}</div>
                  <div style={{ color: "#aaa" }}>
                    {r.start_datetime} – {r.end_datetime}
                    {r.shift && ` (${r.shift})`}
                  </div>
                  <div style={{ color: "#aaa" }}>👥 {r.guests}</div>
                  {r.note && <div style={{ color: "#888", fontStyle: "italic" }}>📝 {r.note}</div>}
                </div>
              ))
            ) : (
              <div style={{ color: "#666" }}>Nema rezervacija</div>
            )}
          </div>
        )}
        {showPrintFloorplan && (
          <FloorplanPrintModal
            date={selectedDate}
            onClose={() => setShowPrintFloorplan(false)}
          />
        )}
      </div>
      {!showPrintFloorplan && (
        <div style={{
          position: "absolute",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 100,
          display: "flex",
          gap: 8,
        }}>
          <button
            onClick={() => setCurrentFloor("inside")}
            style={{
              padding: "6px 16px",
              borderRadius: 8,
              border: "1px solid #c1b428",
              backgroundColor: currentFloor === "inside" ? "#c1b428" : "#1f1f1f",
              color: currentFloor === "inside" ? "black" : "#c1b428",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            🏠 Unutarnji
          </button>
          <button
            onClick={() => setCurrentFloor("outside")}
            style={{
              padding: "6px 16px",
              borderRadius: 8,
              border: "1px solid #c1b428",
              backgroundColor: currentFloor === "outside" ? "#c1b428" : "#1f1f1f",
              color: currentFloor === "outside" ? "black" : "#c1b428",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            🌿 Vanjski
          </button>
        </div>
      )}
    </div>
  );
};

const ViewTable = ({ x, y, table, imgScale, onClick, selectedDate, onTooltip }) => {
  const hoverTimer = React.useRef(null);

  const handleMouseEnter = (e) => {
    const mouseX = e.clientX;
    const mouseY = e.clientY;

    hoverTimer.current = setTimeout(async () => {
      const dateStr = selectedDate.toLocaleDateString("en-CA");
      try {
        const response = await fetch(`/reservations/api/tables/${table.id}/reservations/?date=${dateStr}`);
        const data = await response.json();
        onTooltip({
          visible: true,
          x: mouseX,
          y: mouseY,
          reservations: data.reservations || [],
          tableLabel: table.label,
        });
      } catch {
        onTooltip({ visible: true, x: mouseX, y: mouseY, reservations: [], tableLabel: table.label });
      }
    }, 600); // prikaži tooltip nakon 600ms hovera
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    onTooltip({ visible: false, x: 0, y: 0, reservations: [], tableLabel: "" });
  };

  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: y,
        zIndex: 1,
        cursor: "pointer",
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick(table);
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <TableCard table={table} imgScale={imgScale} />
    </div>
  );
};

const DraggableTable = ({ id, x, y, table, imgScale }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id });

  const finalX = x + (transform?.x || 0);
  const finalY = y + (transform?.y || 0);

  return (
    <div
      ref={setNodeRef}
      style={{
        position: "absolute",
        left: finalX,
        top: finalY,
        zIndex: isDragging ? 999 : 1,
        cursor: isDragging ? "grabbing" : "grab",
      }}
      {...listeners}
      {...attributes}
    >
      <TableCard table={table} imgScale={imgScale} />
    </div>
  );
};

export default Dashboard;